import * as React from "react";
import {
  FullscreenDialog,
  FullscreenDialogContent,
  FullscreenDialogHeader,
  FullscreenDialogBody,
  FullscreenDialogFooter,
  FullscreenDialogTitle,
  FullscreenDialogDescription,
} from "@/components/ui/fullscreen-dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Stacked fullscreen dialogs (e.g. a record edit dialog opened over the inbox
 * message detail) each push their own history entry and each listen for
 * `popstate`. Two module-level mechanisms keep Back and programmatic closes
 * aimed at the right one:
 *
 * - `openStack`: one token per OPEN instance, in open order. Only the top-most
 *   instance handles a `popstate`; the others do nothing (not even re-push), so
 *   one Back closes (or prompts) only the top dialog and history depth never drifts.
 * - `pendingProgrammaticPops`: a close calls `history.back()` to pop its entry;
 *   that `popstate` arrives asynchronously and would otherwise reach the dialog
 *   below as a "Back". The closing instance counts it — only when another open
 *   instance remains to hear it — and the top-most handler swallows counted pops.
 *   It is reset whenever the stack empties so a count can never leak into a
 *   later, unrelated dialog and swallow a real Back.
 */
const openStack: object[] = [];
let pendingProgrammaticPops = 0;

interface FullscreenFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
  /** Label shown on the submit button while submitting. Defaults to the shared `t('saving')`. */
  submittingLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  formId?: string;
  /** Additional buttons rendered between Cancel and Submit (e.g., Preview, Save as Draft) */
  extraFooterButtons?: React.ReactNode;
  /** @deprecated Use onOpenChange instead. Called after close animation completes. */
  onCancel?: () => void;
  /** Called when dialog close is triggered, before onOpenChange(false) */
  onClose?: () => void;
  /** Called when the dialog's close animation finishes and focus is about to be restored.
   *  Use event.preventDefault() to take control of focus (e.g. focus the grid). */
  onCloseAutoFocus?: (event: Event) => void;
  /** Skip the "are you sure?" confirmation when closing. Default: false */
  skipCloseConfirmation?: boolean;
  /** Accessible description for the dialog (rendered visually hidden). Silences the Radix UI warning. */
  description?: string;
  /** Disable the submit button independently of isSubmitting (e.g. form validation) */
  submitDisabled?: boolean;
  /**
   * Omit the footer Submit button — for a fullscreen VIEWER (e.g. the inbox
   * message detail) whose actions live in `extraFooterButtons`. Pair it with
   * `skipCloseConfirmation` (nothing to discard) and a `cancelLabel` such as
   * "Close"; `onSubmit` should then only `preventDefault()`.
   */
  hideSubmit?: boolean;
}

/**
 * FullscreenFormDialog - A reusable fullscreen modal dialog for forms
 *
 * This component provides a consistent fullscreen modal layout with:
 * - Header with logo, title, and close button
 * - Scrollable body content (max-width centered)
 * - Fixed footer with action buttons
 * - Close confirmation dialog on back button, X, Cancel, Escape, and overlay click
 */
export function FullscreenFormDialog({
  open,
  onOpenChange,
  title,
  children,
  onSubmit,
  submitLabel,
  submittingLabel,
  cancelLabel,
  isSubmitting = false,
  formId,
  extraFooterButtons,
  onCancel,
  onClose,
  onCloseAutoFocus,
  skipCloseConfirmation = false,
  description,
  submitDisabled = false,
  hideSubmit = false,
}: Readonly<FullscreenFormDialogProps>) {
  const { t } = useTranslation();
  const historyPushedRef = React.useRef(false);
  // Identity of this instance in the module-level open stack.
  const stackTokenRef = React.useRef<object>({});
  const [showConfirm, setShowConfirm] = React.useState(false);
  // Associate the footer submit button with the body <form> by id. Fall back
  // to a per-instance generated id so two dialogs mounted at once never share
  // one — a shared literal default would make both footer buttons submit the
  // first matching form in the DOM. Hosts may still pass an explicit formId.
  const autoFormId = React.useId();
  const resolvedFormId = formId ?? autoFormId;

  /**
   * Pop the history entry we pushed — but only if it's still the current top entry.
   * A "close-then-navigate" flow (the consumer calls navigate(..., { replace: true })
   * before closing) replaces our entry, which clears `state.fullscreenModal`. In that
   * case we must NOT call back(), or we'd undo the navigation the consumer just made.
   * This makes the restore deterministic — no setTimeout races against an async back().
   */
  const restoreHistory = React.useCallback(() => {
    if (!historyPushedRef.current) return;
    historyPushedRef.current = false;
    if (window.history.state?.fullscreenModal) {
      // Another open dialog will receive this popstate — mark it as ours so it
      // isn't treated as Back. Comparing against our own token makes this
      // independent of whether our stack entry was already removed.
      const token = stackTokenRef.current;
      if (openStack.some((t) => t !== token)) {
        pendingProgrammaticPops += 1;
      }
      window.history.back();
    }
  }, []);

  /** Actually close the dialog — runs callbacks; history is restored by the close effect below */
  const doClose = React.useCallback(() => {
    setShowConfirm(false);
    onClose?.();
    onCancel?.();
    onOpenChange(false);
  }, [onClose, onCancel, onOpenChange]);

  /** Request close — shows confirmation or closes directly */
  const requestClose = React.useCallback(() => {
    if (skipCloseConfirmation) {
      doClose();
    } else {
      setShowConfirm(true);
    }
  }, [skipCloseConfirmation, doClose]);

  // Push a history entry when the dialog opens so the browser Back button closes the
  // modal (via the popstate handler below) instead of navigating away.
  // NOTE: Do NOT add cleanup that resets historyPushedRef here — the close effect
  // below handles the restore when `open` becomes false. Adding cleanup would cause
  // double-push in React Strict Mode.
  React.useEffect(() => {
    if (open && !historyPushedRef.current) {
      window.history.pushState({ fullscreenModal: true }, "");
      historyPushedRef.current = true;
    }
  }, [open]);

  // Register this instance in the open stack while open. Its own effect, keyed
  // only on `open`, so a host re-render (e.g. a new inline onClose) never moves
  // it to the top, and the push/remove pair stays symmetric under Strict Mode.
  // Kept separate from the push-state effect above, which deliberately has no cleanup.
  React.useEffect(() => {
    if (!open) return;
    const token = stackTokenRef.current;
    openStack.push(token);
    return () => {
      const index = openStack.lastIndexOf(token);
      if (index !== -1) openStack.splice(index, 1);
      if (openStack.length === 0) pendingProgrammaticPops = 0;
    };
  }, [open]);

  // Listen for browser back button
  React.useEffect(() => {
    if (!open) return;

    const handlePopState = () => {
      // Only the top-most open dialog reacts to Back.
      if (openStack[openStack.length - 1] !== stackTokenRef.current) return;
      // A popstate caused by another dialog's programmatic close, not a real Back.
      if (pendingProgrammaticPops > 0) {
        pendingProgrammaticPops -= 1;
        return;
      }
      if (!historyPushedRef.current) return;

      // Re-push the history entry immediately to prevent the page behind from navigating.
      // Each back press consumes one entry and re-push adds one, keeping depth at 1.
      window.history.pushState({ fullscreenModal: true }, "");

      if (skipCloseConfirmation) {
        doClose();
      } else {
        setShowConfirm(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [open, skipCloseConfirmation, doClose]);

  // Restore history + reset confirm state when the dialog closes (open -> false).
  // This is the single place a programmatic close (parent sets open=false, e.g. on
  // submit success) gets its pushed entry popped — previously it was leaked, leaving
  // a dead browser-Back press after closing.
  React.useEffect(() => {
    if (!open) {
      setShowConfirm(false);
      restoreHistory();
    }
  }, [open, restoreHistory]);

  // Cleanup: restore history if the component unmounts while still open (e.g. a parent
  // that conditionally mounts the dialog removes it on close without flipping `open`).
  React.useEffect(() => {
    return () => {
      restoreHistory();
    };
  }, [restoreHistory]);

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) requestClose();
        }}
      >
        <FullscreenDialogContent onCloseAutoFocus={onCloseAutoFocus}>
          <FullscreenDialogHeader>
            <div className="flex items-center gap-3">
              <img
                src="/releve_name_white.svg"
                alt="Releve"
                className="h-6 w-6 object-contain"
              />
              <FullscreenDialogTitle>{title}</FullscreenDialogTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={requestClose}
              className="h-8 w-8 text-dialog-header-foreground hover:bg-dialog-header-foreground/20 hover:text-dialog-header-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </FullscreenDialogHeader>
          <FullscreenDialogDescription className="sr-only">
            {description ?? title}
          </FullscreenDialogDescription>

          <FullscreenDialogBody className="p-4 md:p-6">
            {/* The dialog is portalled, but React bubbles synthetic events up
                the COMPONENT tree: opened from inside another form (e.g. a
                picker's "Create new" in a StandardDialog form), this submit
                would also fire the outer form's onSubmit. Stop it here. */}
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                onSubmit(e);
              }}
              id={resolvedFormId}
              className="max-w-6xl mx-auto"
            >
              {children}
            </form>
          </FullscreenDialogBody>

          <FullscreenDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
              disabled={isSubmitting}
            >
              {cancelLabel ?? t('cancel')}
            </Button>
            {extraFooterButtons}
            {!hideSubmit && (
              <Button type="submit" form={resolvedFormId} disabled={isSubmitting || submitDisabled}>
                {isSubmitting ? (submittingLabel ?? t('saving')) : (submitLabel ?? t('save'))}
              </Button>
            )}
          </FullscreenDialogFooter>
        </FullscreenDialogContent>
      </FullscreenDialog>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('closeConfirmation.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('closeConfirmation.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('closeConfirmation.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doClose}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('closeConfirmation.discardChanges')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
